<template>
  <div
    class="external-links"
    :class="{ 'external-links--open': dialogOpen }"
    @mouseenter="scheduleOpenExternalLinksDialog"
    @mouseleave="scheduleCloseExternalLinksDialog"
    @wheel="preventScrollWhenExpanded"
  >
    <button
      class="external-links-button"
      :class="{ 'external-links-button--open': dialogOpen }"
      type="button"
      data-test="header-link-externalLinks"
    >
      <SVGIcon
        class="external-links-button__icon"
        iconName="three-dots"
        alt="Navi"
      />
    </button>

    <ExternalLinksDialog
      v-if="dialogOpen"
      @close-requested="closeExternalLinksDialog"
      class="external-links-dialog"
    />
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount } from "vue";
import ExternalLinksDialog from "@/components/header/ExternalLinksDialog.vue";
import { SVGIcon } from "et-components";

const props = defineProps<{
  dialogOpen?: boolean;
}>();

const emit = defineEmits<{
  (e: "update:dialogOpen", value: boolean): void;
}>();

const openDelay = 100;
const closeDelay = 100;

let openTimeout: ReturnType<typeof setTimeout> | undefined;
let closeTimeout: ReturnType<typeof setTimeout> | undefined;

const clearOpenTimeout = () => {
  if (openTimeout) {
    clearTimeout(openTimeout);
    openTimeout = undefined;
  }
};

const clearCloseTimeout = () => {
  if (closeTimeout) {
    clearTimeout(closeTimeout);
    closeTimeout = undefined;
  }
};

const scheduleOpenExternalLinksDialog = () => {
  clearOpenTimeout();
  clearCloseTimeout();

  openTimeout = setTimeout(() => {
    emit("update:dialogOpen", true);
    openTimeout = undefined;
  }, openDelay);
};

const closeExternalLinksDialog = () => {
  clearOpenTimeout();
  clearCloseTimeout();
  emit("update:dialogOpen", false);
};

const scheduleCloseExternalLinksDialog = () => {
  clearOpenTimeout();
  clearCloseTimeout();

  closeTimeout = setTimeout(() => {
    emit("update:dialogOpen", false);
    closeTimeout = undefined;
  }, closeDelay);
};

onBeforeUnmount(() => {
  clearOpenTimeout();
  clearCloseTimeout();
});

const preventScrollWhenExpanded = (event: WheelEvent) => {
  if (props.dialogOpen) {
    event.preventDefault();
  }
};
</script>

<style scoped lang="scss">
.external-links {
  position: relative;
  flex: 0 0 auto;
  height: 100%;
}

.external-links-dialog {
  position: absolute;
  top: 100%;
  left: 50%;
  z-index: 30;
  transform: translateX(-50%);
}

.external-links-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 5.3rem;
  height: 100%;
  padding: 0;
  border: 0;
  outline: none;
  background: white;
  color: $color-blue-dark;
  cursor: pointer;
  box-sizing: border-box;
}

.external-links-button:hover,
.external-links-button--open {
  background: #f3f4f7;
}

.external-links-button__icon {
  width: 24px;
  height: 24px;
  color: currentColor;
}
</style>
